import { each, isArray, isNil, map, size } from 'lodash';
import moment from 'moment';
import { v4 as uuid } from 'uuid';

export type DatabaseRecordIdentifier = string;

export enum DatabaseType {
  Resource,
  User,
  Document,
}

export interface DatabaseRecordData {
  key?: DatabaseRecordIdentifier;
  id?: DatabaseRecordIdentifier;
  uuid?: DatabaseRecordIdentifier;

  createdAt?: Date | string | moment.Moment;
  updatedAt?: Date | string | moment.Moment;
  deletedAt?: Date | string | moment.Moment;
}

export class DatabaseRecord {
  static recordName = '';
  static objectStoreName = '';
  static fields: string[] = [];
  static autoCreateUUIDs = true;
  static databaseType: DatabaseType = DatabaseType.Document;

  public key: DatabaseRecordIdentifier;
  public createdAt: moment.Moment;
  public updatedAt: moment.Moment;
  public deletedAt: moment.Moment;

  public get lastUpdate(): moment.Moment {
    return moment.max(this.createdAt || moment(0), this.updatedAt || moment(0), this.deletedAt || moment(0));
  }

  get uuid(): DatabaseRecordIdentifier {
    return this.key;
  }

  set uuid(key: DatabaseRecordIdentifier) {
    this.key = key;
  }

  protected static parseDate(date: string): moment.Moment {
    return moment(date);
  }

  constructor(data: DatabaseRecordData = {}) {
    const type = this.constructor as DatabaseModel;

    this.key = data.key || data.id || data.uuid || null;

    if (isNil(this.key) && type.autoCreateUUIDs) {
      this.key = uuid();
      this.createdAt = moment();
    }

    if (type.fields && type.fields.length > 0) {
      each(type.fields, field => {
        this[field] = data[field];

        const fieldName = field.toLowerCase();

        if (fieldName.includes('date') || fieldName.includes('day') || field.endsWith('At')) {
          if (fieldName !== 'dayuuid') {
            this[field] = moment(data[field]);
          }
        }
      });
    }

    if (data.createdAt) {
      this.createdAt = moment(data.createdAt);
    }

    if (data.updatedAt) {
      this.updatedAt = moment(data.updatedAt);
    }

    if (data.deletedAt) {
      this.deletedAt = moment(data.deletedAt);
    }
  }

  get isDirty() {
    return this.isNew || this.isUpdated || this.isDeleted;
  }

  get isNew() {
    return !isNil(this.createdAt);
  }

  get isUpdated() {
    return !isNil(this.updatedAt);
  }

  get isDeleted() {
    return !isNil(this.deletedAt);
  }

  markClean() {
    this.createdAt = null;
    this.updatedAt = null;
    this.deletedAt = null;
  }

  markDirty() {
    if (this.uuid && !this.createdAt) {
      this.markUpdated();
    } else if (!this.createdAt) {
      this.markCreated();
    }
  }

  generateId() {
    this.uuid = uuid();
  }

  markCreated() {
    this.generateId();
    this.createdAt = moment();
  }

  markUpdated() {
    this.updatedAt = moment();
  }

  markDeleted() {
    this.deletedAt = moment();
  }

  toJSON(fields?: string[]): any {
    const object: any = {
      key: this.key,
    };

    if (isNil(fields)) {
      fields = [];
    }

    const type = this.constructor as DatabaseModel;
    if (type.fields && type.fields.length > 0) {
      fields = fields.concat(type.fields);
    }

    each(fields, field => {
      let value = this[field];

      // look at the private cache of foreign records if we never loaded records
      // if (isNil(value) && !isNil(this['_' + field])) {
      //   value =
      // }

      if (!isNil(value)) {
        if (moment.isMoment(value)) {
          value = value.toISOString();
        } else if (isArray(value) && size(value) > 0 && value[0] instanceof DatabaseRecord) {
          value = map(value, record => record.key);
        } else if (value instanceof DatabaseRecord) {
          value = value.toJSON();
        }

        if (!isNil(value)) {
          object[field] = value;
        }
      }
    });

    if (this.createdAt) {
      object.createdAt = this.createdAt.toISOString();
    }

    if (this.updatedAt) {
      object.updatedAt = this.updatedAt.toISOString();
    }

    if (this.deletedAt) {
      object.deletedAt = this.deletedAt.toISOString();
    }

    return object;
  }
}

export type DatabaseModel = typeof DatabaseRecord;
